import React, { createContext, useContext, ReactNode } from 'react';
import { useNotification, NotificationData } from '../hooks/useNotification';
import NotificationContainer from '../components/NotificationContainer';

interface NotificationContextType {
  showSuccess: (message: string, options?: { autoClose?: boolean; duration?: number }) => string;
  showError: (message: string, options?: { autoClose?: boolean; duration?: number }) => string;
  showWarning: (message: string, options?: { autoClose?: boolean; duration?: number }) => string;
  showInfo: (message: string, options?: { autoClose?: boolean; duration?: number }) => string;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const {
    notifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeNotification,
    clearAllNotifications
  } = useNotification();

  const value: NotificationContextType = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeNotification,
    clearAllNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer
        notifications={notifications}
        removeNotification={removeNotification}
      />
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotificationContext must be used within a NotificationProvider');
  }
  return context;
};